import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  machineStatusDisplayLookup,
  websocketError,
} from "../../../helper/reporting/status";
import {
  ReportingMelcoMachineStatisticsModelsMachineListItem,
  ReportingMelcoMachineStatisticsModelsView,
  ReportingMelcoMachineStatisticsModelsViewGroups,
} from "melco-shared-logic";
import { DisplayMachineStatusViewGrouped } from "./DisplayMachineStatusViewGrouped";
import { DisplayMachineStatusViewWithoutGrouped } from "./DisplayMachineStatusViewWithoutGrouped";

type DisplayMachineStatusViewGroupsAndMachinesProps = {
  viewDetailObj: ReportingMelcoMachineStatisticsModelsView;
  activeTooltip: string | null;
  //setActiveTooltip: any;
  setActiveTooltip: React.Dispatch<React.SetStateAction<string | null>>;
};

interface WebSocketUpdate {
  type: string;
  device_id: string;
  timestamp: Date;
  encrypted_customer_id: string;
  melco_serial_number: string;
  data: {
    design: { name: string; stitch_count: number };
    machine: {
      acti_feed: { max: number; min: number };
      needle_numner: number;
      outline: number;
      speed: number;
      run_time: number;
      stitch_number: number;
      stop_type: string;
    };
    status: { state: string };
    //current_machine_state: string;
    // stop_type: string;
  };
}

export const DisplayMachineStatusViewGroupsAndMachies: React.FC<
  DisplayMachineStatusViewGroupsAndMachinesProps
> = ({ viewDetailObj, activeTooltip, setActiveTooltip }) => {
  // const [activeTooltip, setActiveTooltip] = useState<string | null>(null);

  const socketRef = useRef<WebSocket | null>(null);
  const [lastDataReceivedTime, setLastDataReceivedTime] = useState(new Date());
  const [viewDetail, setViewDetail] = useState(viewDetailObj);

  const handleToggleTooltip = (itemIndex: string | null) => {
    setActiveTooltip((prev) => (prev === itemIndex ? null : itemIndex));
  };

  // Effect to check inactivity condition
  useEffect(() => {
    const checkInactivity = () => {
      console.log("CheckInactive");
      setViewDetail((prevData: ReportingMelcoMachineStatisticsModelsView) => {
        // Get the current time
        const currentTime = new Date();
        const currentTimestamp = currentTime.getTime(); // Current time in milliseconds
        // Ensure you're not mutating the previous state directly
        const updatedData = {
          ...prevData,
          group_list: [...(prevData?.group_list ?? [])],
        };

        updatedData.group_list = updatedData?.group_list?.map(
          (group: ReportingMelcoMachineStatisticsModelsViewGroups) => {
            return {
              ...group,
              machine_list: group?.machine_list?.map(
                (
                  machine: ReportingMelcoMachineStatisticsModelsMachineListItem
                ) => {
                  const lastUpdateTime = machine?.last_sync_date
                    ? new Date(machine?.last_sync_date).getTime()
                    : lastDataReceivedTime.getTime(); // Convert timestamp to milliseconds
                  const timeDiff = currentTimestamp - lastUpdateTime;

                  // Check if the data is older than 5 minutes (300000 ms)
                  const isInactive = timeDiff > 2 * 60000; // 5 minutes in milliseconds

                  if (isInactive) {
                    return {
                      ...machine,
                      inactive: isInactive, // Set inactive based on time difference
                      last_status: "",
                      event_type: "NOT_CONNECTED",
                    }; // Update status immutably
                  } else {
                    return {
                      ...machine,
                      inactive: isInactive, // Set inactive based on time difference
                    }; // Update status immutably
                  }
                }
              ),
            };
          }
        );
        // console.log(updatedData);
        return updatedData; // Return the new updated state
      });
    };
    // Call the checkInactivity function immediately on page load
    checkInactivity();
    const interval = setInterval(checkInactivity, 2 * 60000); // Check every 5 min

    return () => clearInterval(interval);
  }, [lastDataReceivedTime]);

  useEffect(() => {
    const connect = (url: string) => {
      console.log("ws - connect");
      console.log(url);

      if (socketRef.current) {
        socketRef.current.onclose = function () {};
        socketRef.current.close();
        socketRef.current = null;
        socketRef.current = new WebSocket(url);
      } else {
        socketRef.current = null;
        socketRef.current = new WebSocket(url);
      }

      socketRef.current.onmessage = async (event: any) => {
        //setMessages((prevMessages) => [event.data]);

        // console.log(JSON.parse(event.data));
        try {
          await handleNewData(JSON.parse(event.data));
        } catch (e) {
          console.log("Error for data ", event?.data);
          console.log("Error handle new data ", e, "   ", JSON.stringify(e));
        }
      };

      socketRef.current.onclose = (event) => {
        // Accessing closure information
        console.log("WebSocket closed:", {
          code: event.code, // Closure code
          reason: event.reason, // Reason for closure
          wasClean: event.wasClean, // Was the closure clean?
        });
        console.log("WebSocket code ", event?.code);
        console.log("WebSocket reason ", event?.reason);
        console.log("WebSocket wasClean ", event?.wasClean);
        console.log("WebSocket error : ", websocketError(event?.code));
        console.log(
          "WebSocket ws - Socket is closed. Reconnect will be attempted in 1 second."
        );
        setTimeout(function () {
          connect(url);
        }, 1000);
      };

      socketRef.current.onerror = (err: any) => {
        console.log(
          "WebSocket ws - Socket encountered error: ",
          err.message,
          "WebSocket ws - Closing socket"
        );
        //console.log(JSON.stringify(err));
        socketRef?.current?.close();
      };
    };

    const initialCall = async () => {
      let deviceIds = await getDeviceIds();

      if (deviceIds.length > 0) {
        console.log(
          "initialCall deviceIds ",
          deviceIds,
          "    ",
          JSON.stringify(deviceIds)
        );
        const localStorageData = JSON.parse(
          localStorage.getItem("MELCO_ACCOUNT") as string
        );
        if (viewDetail?.socket_endpoint) {
          console.log(viewDetail?.socket_endpoint);
          let customerId = localStorageData.userId.replace(/^U0+/, "");

          fetch(viewDetail?.socket_endpoint ?? "", {
            method: "POST",
            headers: {
              "x-api-key": "3a0777b2823b47c94aa4f64cc016fc34",
              "x-customer-id": customerId + "",
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              deviceIds: deviceIds,
            }),
          }).then(async (response) => {
            const result = (await response.json()) as {
              url: string;
              deviceIds: string[];
            };

            connect(result.url);
          });
        } else {
          console.log(
            "initialCall WebSocket Not valid websocket  url ",
            viewDetail?.socket_endpoint
          );
        }
      } else {
        console.log(
          "initialCall No valid deviceIds ",
          deviceIds,
          " ",
          JSON.stringify(deviceIds)
        );
      }
    };

    initialCall();
    // Specify how to clean up after this effect:
    return function cleanup() {
      if (socketRef.current) {
        socketRef.current.onclose = function () {};
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, []);

  const handleNewData = async (newData: WebSocketUpdate) => {
    // Simulate async processing of new data
    //    console.log("newData ", newData);
    const updatedData = await processNewData(newData);
  };

  const processNewData = async (
    newData: WebSocketUpdate
  ): Promise<ReportingMelcoMachineStatisticsModelsView> => {
    // console.log(JSON.stringify(newData));

    return new Promise((resolve) => {
      setTimeout(() => {
        setViewDetail((prevData: ReportingMelcoMachineStatisticsModelsView) => {
          // Get the current time
          // Ensure you're not mutating the previous state directly
          const updatedData = {
            ...prevData,
            group_list: [...(prevData?.group_list ?? [])],
          };

          updatedData.group_list = updatedData?.group_list?.map(
            (group: ReportingMelcoMachineStatisticsModelsViewGroups) => {
              return {
                ...group,
                machine_list: group?.machine_list?.map(
                  (
                    machine: ReportingMelcoMachineStatisticsModelsMachineListItem
                  ) => {
                    if (machine.device_id === newData.device_id) {
                      const machineStateValues = newData?.data?.status?.state
                        ? newData?.data?.status?.state
                            .split(",")
                            .map((value: string) => value.trim()) // Trim spaces
                            .join("-")
                        : ""; // Join with hyphen

                      // Create a lookup key by combining `type` and the trimmed and joined `last status`
                      let lookupKey = "";
                      lookupKey = newData?.type;

                      if (
                        machineStateValues != undefined &&
                        machineStateValues != null &&
                        machineStateValues != ""
                      )
                        lookupKey = lookupKey + "-" + machineStateValues;
                      const result = machineStatusDisplayLookup[lookupKey];
                      // update status only if exist in lookup, otherwise show previous status

                      //console.log("check for  ", machine?.device_id);
                      if (result) {
                        return {
                          ...machine,
                          last_status: newData?.data?.status?.state ?? "",
                          event_type: newData?.type,
                          last_sync_date: newData.timestamp,
                          stop_type: newData?.data?.machine?.stop_type ?? "",
                        };
                      } else {
                        return {
                          ...machine,
                          stop_type: newData?.data?.machine?.stop_type ?? "",
                          last_sync_date: newData.timestamp,
                        }; // Update status immutably
                      }
                    }

                    return machine;
                  }
                ),
              };
            }
          );
          resolve(updatedData);
          return updatedData; // Return the new updated state
        });

        // resolve(viewDetail);
      }, 500); // Simulated processing time
    });
  };

  const getDeviceIds = async () => {
    let deviceIds: string[] = [];
    // Iterate through each group in the group_list
    viewDetail?.group_list?.forEach(
      (group: ReportingMelcoMachineStatisticsModelsViewGroups) => {
        // Iterate through each machine in the machine_list of the group
        group?.machine_list?.forEach(
          (device: ReportingMelcoMachineStatisticsModelsMachineListItem) => {
            // Add device_id to the list
            // is_discontinued means now this machine does not belong to this user.
            // it shifted to some other user
            if (!device?.is_discontinued) {
              deviceIds.push(device?.device_id ?? "");
            }
          }
        );
      }
    );

    const uniqueDeviceIds: string[] = [];
    deviceIds.forEach((item) => {
      if (!uniqueDeviceIds.includes(item)) {
        uniqueDeviceIds.push(item);
      }
    });
    return uniqueDeviceIds;
  };

  return (
    <>
      {viewDetail?.grouped_display ? (
        <DisplayMachineStatusViewGrouped
          activeTooltip={activeTooltip}
          onToggleTooltip={handleToggleTooltip}
          viewDetail={viewDetail}
        />
      ) : (
        <>
          <DisplayMachineStatusViewWithoutGrouped
            activeTooltip={activeTooltip}
            onToggleTooltip={handleToggleTooltip}
            viewDetail={viewDetail}
          />
        </>
      )}
    </>
  );
};
